import {
  EmailVerificationSendRequest,
  EmailVerificationSendResponse,
  EmailVerificationVerifyRequest,
  EmailVerificationVerifyResponse,
  EmailVerificationResult
} from "../my-type";
import { authUserData } from ".";
import {
  UserService as GeneratedUserService,
  type SendVerificationCodeRequest,
  type VerifyEmailCodeRequest,
  callApi,
  configureApiClient,
} from "../api/client";

type ApiClientError = Error & { body?: unknown };

const configureEmailVerificationApiClient = () => {
  configureApiClient({
    tokenProvider: () => authUserData()?.jwtToken ?? null,
  });
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const body = (error as ApiClientError)?.body as
    | { message?: string; detail?: string }
    | string
    | undefined;

  if (typeof body === "string" && body.trim()) {
    return body;
  }
  if (body && typeof body === "object") {
    if (typeof body.message === "string" && body.message.trim()) {
      return body.message;
    }
    if (typeof body.detail === "string" && body.detail.trim()) {
      return body.detail;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
};

/**
 * Sends a 6-digit verification code to the specified email address
 * @param email - Email address to send verification code to
 * @param userId - Optional user ID if verification is for existing user
 * @returns Promise with success status and response data
 */
export const sendVerificationCode = async (
  email: string,
  userId?: number
): Promise<EmailVerificationResult<EmailVerificationSendResponse>> => {
  try {
    const requestBody: EmailVerificationSendRequest = { email };
    if (userId) {
      requestBody.userId = userId;
    }
    configureEmailVerificationApiClient();
    const data = await callApi(
      () =>
        GeneratedUserService.postApiUserSendVerificationCode(
          requestBody as SendVerificationCodeRequest
        ),
      "Failed to send verification code. Please try again later."
    );

    return {
      success: true,
      data: data as EmailVerificationSendResponse
    };
  } catch (error: unknown) {
    console.error("Send verification code error:", error);

    const errorMessage = getErrorMessage(
      error,
      "Failed to send verification code. Please try again later."
    );

    return {
      success: false,
      data: { message: errorMessage },
      error: errorMessage
    };
  }
};

/**
 * Verifies the submitted verification code for the given email address
 * @param email - Email address that received the verification code
 * @param code - 6-digit verification code
 * @returns Promise with success status and verification result
 */
export const verifyEmailCode = async (
  email: string,
  code: string
): Promise<EmailVerificationResult<EmailVerificationVerifyResponse>> => {
  try {
    const requestBody: EmailVerificationVerifyRequest = { email, code };
    configureEmailVerificationApiClient();
    const data = await callApi(
      () =>
        GeneratedUserService.postApiUserVerifyEmailCode(
          requestBody as VerifyEmailCodeRequest
        ),
      "Invalid or expired verification code"
    );

    return {
      success: true,
      data: data as EmailVerificationVerifyResponse
    };
  } catch (error: unknown) {
    console.error("Verify email code error:", error);

    const errorMessage = getErrorMessage(error, "Invalid or expired verification code");

    return {
      success: false,
      data: {
        message: errorMessage,
        isValid: false
      },
      error: errorMessage
    };
  }
};

/**
 * Client-side email validation
 * @param email - Email address to validate
 * @returns True if email format is valid
 */
export const validateEmailFormat = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Client-side verification code validation
 * @param code - Verification code to validate
 * @returns True if code format is valid (6 digits)
 */
export const validateVerificationCode = (code: string): boolean => {
  return /^\d{6}$/.test(code);
};
